"use client";

import { useMemo } from "react";
import { useLocale, useTranslations } from "next-intl";
import type { Locale } from "@mw/shared/i18n";
import type { BillingPeriod } from "@mw/shared/pricing";
import { getPriceForCurrency } from "@mw/shared/pricing";
import { EventName } from "@mw/shared/analyticsEventNames";
import { useCurrency } from "@/app/_providers/currency";
import { formatPrice } from "@utilities/numbers";

import IconCircleCheck from "@ui/central-icons/icons/IconCircleCheck";
import { iconStrokeFix } from "@ui/central-icons/iconUtils";
import { LinkTracked } from "@ui/components/core/LinkTracked";
import { TIER_COVER_IMAGES } from "../../utils";
import { CalculatorResultCardShell } from "./CalculatorResultCardShell";
import { CalculatorResultCardAnimator } from "./CalculatorResultCardAnimator";
import { type PaygProductOption, computePaygCostUsd, recommendSubscriptionTier } from "../payg-pricing-data";
import { type CalculatorMode } from "../../PricingTableContext";
import { formatUnitPrice, formatUsage, getPriceSuffixText, getUnitShort } from "./calculatorUtils";
import { appUrls } from "@/constants";

const PAYG_CTA_HREF = appUrls.developers;

function CardBullet({ children }: { children: React.ReactNode }) {
  return (
    <li className='tw-flex tw-items-center tw-gap-2 tw-text-white/80 tw-type-sm'>
      <IconCircleCheck className={`tw-size-4 tw-text-white/60 tw-flex-none ${iconStrokeFix}`} />
      <span>{children}</span>
    </li>
  );
}

export interface PaygResultCardProps {
  product: PaygProductOption;
  usage: number;
}

export function PaygResultCard({ product, usage }: PaygResultCardProps) {
  const t = useTranslations("PricingClientKeys.PaygCostCalculator");
  const locale = useLocale() as Locale;
  const { currency, currencySymbol } = useCurrency();

  const estimatedCostUsd = computePaygCostUsd(product, usage);
  const formattedCost = formatPrice(getPriceForCurrency(estimatedCostUsd, currency), currencySymbol, locale);
  const productLabel = t(`products.${product.id}` as Parameters<typeof t>[0]);
  const unitShort = getUnitShort(t, product.unit);
  const formattedUsage = formatUsage(usage, unitShort);

  const pricePerUnitConverted = getPriceForCurrency(product.pricePerUnit, currency);
  const formattedUnitPrice = formatUnitPrice(pricePerUnitConverted, currencySymbol);
  const priceSuffix = getPriceSuffixText(t, product.unit, formattedUnitPrice);

  return (
    <CalculatorResultCardShell bg='black' coverImage={product.coverImage}>
      <div className='tw-mb-4 lg:tw-mb-6'>
        <p className='tw-text-white/60 tw-type-xs tw-mb-2'>{t("estimated_cost")}</p>
        <h3 className='tw-text-white tw-type-5xl tw-tabular-nums'>{formattedCost}</h3>
      </div>

      <ul className='tw-space-y-2 lg:tw-space-y-3 tw-mb-6 lg:tw-mb-0'>
        <CardBullet>{t("bullet_payg_usage_at_price", { usage: formattedUsage, price: priceSuffix })}</CardBullet>
        <CardBullet>{t("bullet_product_models", { product: productLabel })}</CardBullet>
        <CardBullet>{t("bullet_payg_tagline")}</CardBullet>
      </ul>

      <div className='tw-mt-auto lg:-tw-mx-5 lg:-tw-mb-5'>
        <LinkTracked
          eventName={EventName.mw_pricing_card_cta_clicked}
          eventProperties={{ tier: "payg", source: "api_payg_calculator" }}
          asButton
          buttonVariants={{ variant: "elevated", size: "lg", expand: true }}
          className='!tw-rounded-2xl'
          href={PAYG_CTA_HREF}
          label={t("cta_label")}
        />
      </div>
    </CalculatorResultCardShell>
  );
}

export interface SubscriptionResultCardProps {
  product: PaygProductOption;
  usage: number;
  period: BillingPeriod;
}

export function SubscriptionResultCard({ product, usage, period }: SubscriptionResultCardProps) {
  const t = useTranslations("PricingClientKeys.PaygCostCalculator");
  const tCard = useTranslations("PricingClientKeys.PricingCard");

  const recommendation = useMemo(() => recommendSubscriptionTier(product, usage, period), [product, usage, period]);
  const { tier, monthlyPrice, paygValueUnits, isEnterprise } = recommendation;

  const tierName = tCard(`tiers.${tier.planId}.title` as Parameters<typeof tCard>[0]);
  const coverImage = TIER_COVER_IMAGES[tier.planId] ?? TIER_COVER_IMAGES.payg;

  const locale = useLocale() as Locale;
  const { currency, currencySymbol } = useCurrency();

  const formattedPrice = monthlyPrice
    ? formatPrice(getPriceForCurrency(monthlyPrice, currency), currencySymbol, locale)
    : null;

  const ctaHref = tier.cta.href;
  const ctaLabel = isEnterprise ? t("subscription_enterprise_cta") : t("cta_label_choose_tier", { tier: tierName });

  const includedBullet =
    paygValueUnits !== undefined
      ? t("bullet_subscription_includes_units", { units: formatUsage(paygValueUnits, getUnitShort(t, product.unit)) })
      : t("bullet_subscription_custom_volume");

  const productLabel = t(`products.${product.id}` as Parameters<typeof t>[0]);

  return (
    <CalculatorResultCardShell bg='black' coverImage={coverImage}>
      <div className='tw-mb-4 lg:tw-mb-6'>
        <p className='tw-text-white/60 tw-type-xs tw-mb-2'>{tCard("calculator.recommended_plan")}</p>
        <h3 className='tw-text-white tw-type-5xl'>{tierName}</h3>
        {formattedPrice && (
          <p className='tw-text-white/80 tw-type-base tw-mt-2 tw-tabular-nums'>
            {formattedPrice} {period === "yearly" ? t("per_month_yearly_short") : t("per_month_short")}
          </p>
        )}
      </div>

      <ul className='tw-space-y-2 lg:tw-space-y-3 tw-mb-6 lg:tw-mb-0'>
        <CardBullet>{includedBullet}</CardBullet>
        <CardBullet>{t("bullet_product_models", { product: productLabel })}</CardBullet>
        <CardBullet>{t("bullet_subscription_tagline")}</CardBullet>
      </ul>

      <div className='tw-mt-auto lg:-tw-mx-5 lg:-tw-mb-5'>
        <LinkTracked
          eventName={EventName.mw_pricing_card_cta_clicked}
          eventProperties={{ tier: tier.planId, source: "api_subscription_calculator" }}
          asButton
          buttonVariants={{ variant: "elevated", size: "lg", expand: true }}
          className='!tw-rounded-2xl'
          href={ctaHref}
          label={ctaLabel}
        />
      </div>
    </CalculatorResultCardShell>
  );
}

export interface ApiCalculatorResultCardProps {
  mode: CalculatorMode;
  product: PaygProductOption;
  usage: number;
  period: BillingPeriod;
}

export function ApiCalculatorResultCard({ mode, product, usage, period }: ApiCalculatorResultCardProps) {
  return (
    <CalculatorResultCardAnimator animationKey={`${mode}-${product.id}`} layoutId='api-calculator-result-card'>
      {mode === "payg" ? (
        <PaygResultCard product={product} usage={usage} />
      ) : (
        <SubscriptionResultCard product={product} usage={usage} period={period} />
      )}
    </CalculatorResultCardAnimator>
  );
}
